import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { colors } from '../../styles/colors';
import LoginPreferencesBar from './LoginPreferencesBar';
import { authAPI, clearOfflineQueueOnSessionEnd } from '../../services/api';
import { useAuth } from '../../services/AuthContext';
import {
  applyPreferencesForUser,
  loadAndApplyUserPreferences
} from '../../services/userPreferences';
import PasswordInput from '../Common/PasswordInput';
import SupportAlertBanner from '../Common/SupportAlertBanner';
import usePublicPagesSettings from '../../hooks/usePublicPagesSettings';
import MessageModal from '../Common/MessageModal';

// Animations de base
const fadeIn = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(30px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
`;

const slideIn = keyframes`
  from { 
    transform: translateX(-100%); 
    opacity: 0; 
  }
  to { 
    transform: translateX(0); 
    opacity: 1; 
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// FLUX IMPERCEPTIBLE : Mouvement ultra-lent d'un gradient géant
const subtleFlow = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
`;

// Container principal avec image de fond
const LoginContainer = styled.div`
  min-height: 100vh;
  background: url('/images/background.jpg') center/cover no-repeat fixed;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  /* ✅ FLUIDITÉ IMPERCEPTIBLE : Gradient géant avec 80 stops */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    /* ✅ GRADIENT LINEAR GÉANT - 80 stops couvrant TOUTES les nuances de bleu */
    background: linear-gradient(135deg,
      hsl(180, 75%, 50%) 0%, hsl(181, 76%, 50%) 1.25%, hsl(182, 77%, 51%) 2.5%,
      hsl(183, 78%, 51%) 3.75%, hsl(184, 79%, 51%) 5%, hsl(185, 80%, 51%) 6.25%,
      hsl(186, 81%, 51%) 7.5%, hsl(187, 82%, 51%) 8.75%, hsl(188, 83%, 52%) 10%,
      hsl(189, 84%, 52%) 11.25%, hsl(190, 85%, 52%) 12.5%, hsl(191, 84%, 52%) 13.75%,
      hsl(192, 83%, 52%) 15%, hsl(193, 82%, 52%) 16.25%, hsl(194, 81%, 52%) 17.5%,
      hsl(195, 80%, 52%) 18.75%, hsl(196, 81%, 52%) 20%, hsl(197, 82%, 52%) 21.25%,
      hsl(198, 83%, 52%) 22.5%, hsl(199, 84%, 52%) 23.75%, hsl(200, 85%, 52%) 25%,
      hsl(201, 86%, 52%) 26.25%, hsl(202, 87%, 52%) 27.5%, hsl(203, 86%, 52%) 28.75%,
      hsl(204, 85%, 52%) 30%, hsl(205, 84%, 52%) 31.25%, hsl(206, 83%, 52%) 32.5%,
      hsl(207, 82%, 52%) 33.75%, hsl(208, 81%, 52%) 35%, hsl(209, 80%, 52%) 36.25%,
      hsl(210, 81%, 52%) 37.5%, hsl(211, 82%, 52%) 38.75%, hsl(212, 83%, 52%) 40%,
      hsl(213, 84%, 52%) 41.25%, hsl(214, 85%, 52%) 42.5%, hsl(215, 84%, 52%) 43.75%,
      hsl(216, 83%, 52%) 45%, hsl(217, 82%, 52%) 46.25%, hsl(218, 81%, 52%) 47.5%,
      hsl(219, 80%, 52%) 48.75%, hsl(220, 81%, 52%) 50%, hsl(221, 82%, 52%) 51.25%,
      hsl(222, 83%, 52%) 52.5%, hsl(223, 84%, 52%) 53.75%, hsl(224, 85%, 52%) 55%,
      hsl(225, 84%, 52%) 56.25%, hsl(226, 83%, 52%) 57.5%, hsl(227, 82%, 52%) 58.75%,
      hsl(228, 81%, 52%) 60%, hsl(229, 80%, 52%) 61.25%, hsl(230, 79%, 52%) 62.5%,
      hsl(231, 78%, 52%) 63.75%, hsl(232, 77%, 52%) 65%, hsl(233, 76%, 52%) 66.25%,
      hsl(234, 75%, 52%) 67.5%, hsl(235, 76%, 52%) 68.75%, hsl(236, 77%, 52%) 70%,
      hsl(237, 78%, 52%) 71.25%, hsl(238, 79%, 52%) 72.5%, hsl(239, 80%, 52%) 73.75%,
      hsl(240, 81%, 52%) 75%, hsl(241, 80%, 52%) 76.25%, hsl(242, 79%, 52%) 77.5%,
      hsl(243, 78%, 52%) 78.75%, hsl(244, 77%, 52%) 80%, hsl(245, 76%, 52%) 81.25%,
      hsl(246, 75%, 52%) 82.5%, hsl(247, 76%, 52%) 83.75%, hsl(248, 77%, 52%) 85%,
      hsl(249, 78%, 52%) 86.25%, hsl(250, 79%, 52%) 87.5%, hsl(251, 78%, 52%) 88.75%,
      hsl(252, 77%, 52%) 90%, hsl(253, 76%, 52%) 91.25%, hsl(254, 75%, 52%) 92.5%,
      hsl(255, 74%, 52%) 93.75%, hsl(256, 73%, 52%) 95%, hsl(257, 74%, 52%) 96.25%,
      hsl(258, 75%, 52%) 97.5%, hsl(259, 76%, 52%) 98.75%, hsl(260, 77%, 52%) 100%
    );
    /* ✅ Gradient 3× plus large pour mouvement fluide */
    background-size: 300% 300%;
    /* ✅ ANIMATION FLUIDE ET PERCEPTIBLE : 15 secondes par direction */
    animation: ${subtleFlow} 15s linear infinite alternate;
    opacity: 0.85;
    will-change: background-position;
  }
`;

// Boîte de login avec animations
const LoginBox = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  width: 100%;
  max-width: 500px;
  z-index: 2;
  position: relative;
  animation: ${fadeIn} 0.8s ease-out;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin: 20px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    margin: 15px;
  }
`;

// Logo avec animation subtle
const Logo = styled.img`
  width: 220px;
  height: 220px;
  margin: 0 auto 1.5rem;
  display: block;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.white};
  padding: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  animation: ${pulse} 3s ease-in-out infinite;
  
  &:hover {
    transform: scale(1.05) rotate(2deg);
    box-shadow: 0 15px 40px rgba(52, 152, 219, 0.4);
  }
  
  @media (max-width: 768px) {
    width: 180px;
    height: 180px;
  }
  
  @media (max-width: 480px) {
    width: 150px;
    height: 150px;
  }
`;

// Titre avec animation
const Title = styled.h1`
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  margin-bottom: 2rem;
  font-size: 2.5rem;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  animation: ${slideIn} 0.6s ease-out 0.2s both;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 1.2rem;
  }
`;

// Formulaire responsive
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  width: 100%;
  
  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

// Champs de saisie avec hover effects
const Input = styled.input`
  padding: 14px 16px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 1.1rem;
  background: rgba(255, 255, 255, 0.1) !important;
  color: ${({ theme }) => theme.colors.white};
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2);
    transform: translateY(-2px);
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
  
  /* Forcer transparence pour autocomplete Chrome */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.colors.white} !important;
    background-color: transparent !important;
    background: rgba(255, 255, 255, 0.1) !important;
    transition: background-color 5000s ease-in-out 0s;
  }
  
  @media (max-width: 768px) {
    padding: 12px 14px;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 11px 13px;
    font-size: 0.95rem;
  }
`;

// Bouton avec dégradé animé et hover
const Button = styled.button`
  background: linear-gradient(135deg, rgba(9, 153, 1, 1) 0%,  rgba(7, 125, 0, 1) 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 3s ease infinite;
  color: ${({ theme }) => theme.colors.white};
  padding: 14px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  position: relative;
  overflow: hidden;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: 0.5s;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(46, 204, 113, 0.4);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.gray};
    cursor: not-allowed;
    transform: none;
    animation: none;
  }
  
  @media (max-width: 768px) {
    padding: 13px;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    font-size: 0.95rem;
  }
`;

// Loader animé
const Loader = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 10px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Messages d'erreur améliorés
const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.1);
  color: ${({ theme }) => theme.colors.error};
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
  border: 1px solid rgba(231, 76, 60, 0.3);
  animation: ${fadeIn} 0.3s ease-out;
  width: 100%;
  box-sizing: border-box;
  
  &::before {
    content: '⚠️ ';
    margin-right: 8px;
  }
`;

const SessionInfoMessage = styled.div`
  background: rgba(243, 156, 18, 0.14);
  color: #f39c12;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
  border: 1px solid rgba(243, 156, 18, 0.4);
  animation: ${fadeIn} 0.3s ease-out;
  width: 100%;
  box-sizing: border-box;

  &::before {
    content: '⏱️ ';
    margin-right: 8px;
  }
`;

// Footer responsive
const Footer = styled.div`
  text-align: center;
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: ${({ theme }) => theme.colors.white};
  font-size: 0.9rem;
  opacity: 0.8;
  animation: ${fadeIn} 0.6s ease-out 0.4s both;
  width: 100%;

  a {
    color: ${({ theme }) => theme.colors.white};
    text-decoration: none;
    margin: 0 0.5rem;
    transition: color 0.3s ease;
    
    &:hover {
      color: ${({ theme }) => theme.colors.primaryDark};
      text-decoration: underline;
    }
  }
  
  @media (max-width: 768px) {
    margin-top: 2rem;
    padding-top: 1.2rem;
    font-size: 0.85rem;
  }
  
  @media (max-width: 480px) {
    margin-top: 1.5rem;
    padding-top: 1rem;
    font-size: 0.8rem;
  }
`;

const ForgotPasswordLink = styled(Link)`
  display: inline-block;
  margin-top: 8px;
  text-align: right;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 600;
  &:hover {
    color: #ffffff;
    text-decoration: underline;
  }
`;

const Login = () => {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionInfo, setSessionInfo] = useState('');
  const [disabledModalOpen, setDisabledModalOpen] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { settings: publicPages } = usePublicPagesSettings({ publicAccess: true });

  const isTimeoutError = (err) => {
    const msg = String(err?.message || '').toLowerCase();
    const code = String(err?.code || '').toUpperCase();
    return code === 'ECONNABORTED' || msg.includes('timeout');
  };

  const loginWithRetry = async (payload, retriesLeft = 1) => {
    try {
      return await authAPI.login(payload);
    } catch (err) {
      if (isTimeoutError(err) && retriesLeft > 0) {
        await new Promise((resolve) => setTimeout(resolve, 700));
        return loginWithRetry(payload, retriesLeft - 1);
      }
      throw err;
    }
  };

  // ✅ PLUS BESOIN DE JAVASCRIPT ! Tout est géré par l'animation CSS !
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reasonFromUrl = params.get('logout_reason');
    const reasonFromStorage = sessionStorage.getItem('ucp_logout_reason');
    const reasonFromLocal = localStorage.getItem('ucp_last_logout_reason');
    const atFromLocal = Number(localStorage.getItem('ucp_last_logout_at'));
    const isFreshLocalReason = Number.isFinite(atFromLocal) && (Date.now() - atFromLocal) < 15 * 60 * 1000;
    const reason = reasonFromUrl || reasonFromStorage || (isFreshLocalReason ? reasonFromLocal : null);

    if (reason === 'inactivity') {
      setSessionInfo(t('auth.sessionExpired'));
    }
    sessionStorage.removeItem('ucp_logout_reason');
    sessionStorage.removeItem('ucp_logout_at');
    localStorage.removeItem('ucp_last_logout_reason');
    localStorage.removeItem('ucp_last_logout_at');
    if (reasonFromUrl) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [t]);

  // Lot 6 — file offline résiduelle sans session (renfort iOS / Safari PWA).
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token || !String(token).trim()) {
      clearOfflineQueueOnSessionEnd();
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSessionInfo('');

    try {
      const response = await loginWithRetry(credentials, 1);
      
      console.log('🔍 [Login] Full response:', response);
      console.log('🔍 [Login] response.data:', response.data);
      console.log('🔍 [Login] response.data.user:', response.data.user);
      console.log('🎨 [Login] code_couleur reçu:', response.data.user?.code_couleur);
      
      if (response.status === 200 && response.data.token) {
        if (response.data.preferences) {
          applyPreferencesForUser(response.data.user, response.data.preferences);
        } else {
          await loadAndApplyUserPreferences(response.data.user);
        }
        login(response.data.user, response.data.token);
        
        console.log('✅ [Login] Calling login() with user:', response.data.user);
        
        // Redirection selon le rôle
        setTimeout(() => {
          switch (response.data.user.role) {
            case 'superadmin':
              navigate('/dashboard', { replace: true });
              break;
            case 'admin':
              navigate('/admin/dashboard', { replace: true });
              break;
            case 'intervenant':
              navigate('/intervenant', { replace: true });
              break;
            case 'client':
              navigate('/client', { replace: true });
              break;
            default:
              navigate('/dashboard', { replace: true });
          }
        }, 500);
      } else {
        setError(response.data.message || t('auth.loginFailed'));
      }
    } catch (err) {
      // L'API retourne { error: { code, message } } - extraire le message
      let errorMsg = t('auth.genericError');
      const statusCode = Number(err?.response?.status);
      const backendCode = String(err?.response?.data?.error?.code || '').trim();
      
      if (err.response?.data?.error) {
        const errorData = err.response.data.error;
        // Si error est un objet avec message, extraire le message
        errorMsg = typeof errorData === 'object' ? errorData.message : errorData;
      } else if (err.message) {
        errorMsg = err.message;
      }

      if (isTimeoutError(err)) {
        errorMsg = t('auth.timeoutError');
      }

      if (statusCode === 403 && (backendCode === 'ACCOUNT_DISABLED' || backendCode === 'ACCOUNT_REVOKED')) {
        setDisabledModalOpen(true);
        setError('');
        return;
      }

      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginBox>
        <SupportAlertBanner banners={publicPages?.support?.banners} />
        <Logo
          src="/images/logo.apng"
          alt="Universal Cold Plannit®"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />

        <Title>Universal Cold Plannit®</Title>

        <Form onSubmit={handleSubmit}>
          <Input
            type="email"
            placeholder={t('auth.emailPlaceholder')}
            value={credentials.email}
            onChange={(e) => setCredentials({...credentials, email: e.target.value})}
            required
            disabled={loading}
            aria-label={t('auth.email')}
          />

          <PasswordInput
            theme="dark"
            placeholder={t('auth.passwordPlaceholder')}
            value={credentials.password}
            onChange={(e) => setCredentials({...credentials, password: e.target.value})}
            required
            disabled={loading}
            aria-label={t('auth.password')}
          />
          <ForgotPasswordLink to="/forgot-password">{t('auth.forgotPassword')}</ForgotPasswordLink>

          {sessionInfo && <SessionInfoMessage>{sessionInfo}</SessionInfoMessage>}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Button type="submit" disabled={loading} aria-busy={loading}>
            {loading ? (
              <>
                <Loader />
                {t('auth.submitting')}
              </>
            ) : (
              `${t('auth.submit')} ❄️`
            )}
          </Button>
        </Form>

        <Footer>
          <p>{t('auth.footerVersion')}</p>
          <p>
            <a href="/support">{t('auth.support')}</a> • 
            <a href="/confidentialite">{t('auth.privacy')}</a>
          </p>
        </Footer>
      </LoginBox>

      <MessageModal
        isOpen={disabledModalOpen}
        onClose={() => setDisabledModalOpen(false)}
        title={t('auth.accountDisabledTitle')}
        message={t('auth.accountDisabledMessage')}
        type="warning"
        variant="auth"
      />
    </LoginContainer>
  );
};

export default Login;